import React, { Component, Fragment } from "react";
import { Check, Pencil } from "lucide-react";
import { Col, Label, Row } from "reactstrap";
import { StatusBadge, LoadingDots } from "../../components/ui";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { SelectItem } from "../../models/SelectItem";
import moment from "moment";
import { toastService } from "../../services/toastService";
import { User, UserAddress, UserProfileUpdate } from "../../models/User";
import { MultiSelect } from "primereact/multiselect";
import { userApiService } from "../../services/api/users/UsersApiService";
import _ from "lodash";
import { Button } from "primereact/button";
import history from "../../history";
import ExtendedDropdown from "../../components/ExtendedDropdown";
import ExtendedInputText from "../../components/ExtendedInputText";
import AddUserAddress from "./UserAddressUpdate";
import { isUserProfileUpdateAllowed } from "../../authGuard";
import { utils, countryCodeToFlag } from "../../utils/utils";
import StructuredAddressComponent from "./StructuredAddressComponent";
import ResidencePermitSection from "./ResidencePermitSection";
import { structuredAddressApiService } from "../../services/api/structuredAddress/StructuredAddressApiService";
import { isComplianceOfficer, isComplianceOfficerApprover, isSuperAdmin } from "../../authGuard";

interface GeneralDataProps {
    userData: User | null;
    groupsSelectItem: SelectItem<number>[];
    kycStatusTypes: Map<number, string>;
}
interface GeneralDataState {
    isLoading: boolean;
    userData: User | null;
    isAddressDetailVisible: boolean;
    selectedAddress: UserAddress | null;
    multiSelectLimit: number;
    showStructuredAddress: boolean;
    structuredAddressLoading: boolean;
}

export default class GeneralDataComponent extends Component<GeneralDataProps, GeneralDataState> {
    validationErrors: Map<string, boolean> = new Map<string, boolean>();
    countryNames: Map<string, string> = new Map<string, string>();
    previousState: GeneralDataState | null = null;

    constructor(props: GeneralDataProps | Readonly<GeneralDataProps>) {
        super(props);
        this.state = {
            // Starts true: componentDidMount sets it anyway, and a first render with the form would mount
            // the sections (Residence Permit) only to unmount them behind the loader and mount them again,
            // firing their GETs twice when the General tab is reopened with the user already loaded.
            isLoading: true,
            userData: this.props.userData,
            multiSelectLimit: 5,
            isAddressDetailVisible: false,
            selectedAddress: null,
            showStructuredAddress: false,
            structuredAddressLoading: true, 
        };
        this.previousState = JSON.parse(JSON.stringify(this.state));
    }
    genderSelectItem = [
        { label: "Male", value: "M" },
        { label: "Female", value: "F" },
    ];
    sourceOfFunds: SelectItem[] = [];

    countries: SelectItem[] = [];

    async componentDidMount() {
        this.setState({ isLoading: true });

        const countriesResult = await utils.getCountriesResult();
        if (!countriesResult.success) {
            toastService.error(countriesResult.message);
            this.setState({ isLoading: false });
            return;
        }

        this.countries = utils.getCountries();

        const sourceOfFunds = await userApiService.getSourceOfFunds();

        if (!sourceOfFunds.success) {
            toastService.error(sourceOfFunds.message);
            // Like the countries branch above: clear the loader, or the tab stays on a spinner for good.
            this.setState({ isLoading: false });
            return;
        }

        this.sourceOfFunds = sourceOfFunds.data;
        
        // Check structured address availability
        await this.checkStructuredAddressAvailability();
        
        this.setState({ isLoading: false });
    }

    componentDidUpdate(prevProps: Readonly<GeneralDataProps>, prevState: Readonly<GeneralDataState>, snapshot?: any): void {
        const userDataChanged = prevState.userData?.id !== this.state.userData?.id;
        
        this.state.userData == null ? this.setState({ userData: this.props.userData }) : this.setState({ userData: this.state.userData });
        
        // Re-check structured address availability if user data changed
        if (userDataChanged && this.state.userData?.id) {
            this.checkStructuredAddressAvailability();
        }
        
        this.previousState = JSON.parse(JSON.stringify(this.state));
    }

    shouldComponentUpdate(nextProps: Readonly<GeneralDataProps>, nextState: Readonly<GeneralDataState>, nextContext: any): boolean {
        return !_.isEqual(this.props, nextProps) || !_.isEqual(this.previousState, nextState);
    }

    onFieldUpdate = (fieldName: string, value: any) => {
        const userData = this.state.userData as any;
        userData[fieldName] = value;
        this.setState({ userData: userData });
    };

    updateNationalities = (value: any) => {
        const userData = this.state.userData;
        userData!.nationalities = value;
        if (userData!.nationalities.length > this.state.multiSelectLimit) {
            toastService.error(`Select only ${this.state.multiSelectLimit} Nationalities`, "Warning");
        } else {
            this.setState({ userData: userData });
        }
    };

    updateTaxResidence = (value: any) => {
        const userData = this.state.userData;
        userData!.taxResidences = value;
        if (userData!.taxResidences.length > this.state.multiSelectLimit) {
            toastService.error(`Select only ${this.state.multiSelectLimit} Tax Residences`, "Warning");
        } else {
            this.setState({ userData: userData });
        }
    };

    countryOptionTemplate(option: SelectItem) {
        return (
            <div className="flex items-center gap-2">
                <span className="text-base leading-none">{countryCodeToFlag(option.value)}</span>
                <span>{option.label}</span>
            </div>
        );
    }

    renderDateBadge = (date: Date) => {
        if (date == null) {
            return StatusBadge(false, "Pending");
        }

        return (
            <>
                {StatusBadge(true, "Accepted")}
                {` at ${moment.utc(date).local().format("DD-MM-YYYY")}`}
            </>
        );
    };

    onSubmitButtonClick = async () => {
        this.setState({ isLoading: true });

        const newData = this.state.userData as User;

        const objToPost: UserProfileUpdate = {
            id: newData.id,
            firstName: newData.firstName,
            lastName: newData.lastName,
            nonLatinFirstName: newData.nonLatinFirstName,
            nonLatinLastName: newData.nonLatinLastName,
            email: newData.email,
            photo: newData.photo,
            birthDate: newData.birthDate,
            phoneNumber: newData.phoneNumber,
            cryptoCustomer: newData.cryptoCustomer,
            sourceOfFundId: newData.sourceOfFundId,
            residenceAddress: newData.residenceAddress,
            correspondenceAddress: newData.correspondenceAddress,
            nationalities: newData.nationalities,
            taxResidences: newData.taxResidences,
            nationalIdNumber: newData.nationalIdNumber,
            primaryLanguage: newData.primaryLanguage,
            secondaryLanguage: newData.secondaryLanguage,
            merchantGroupId: newData.merchantGroupId,
            accountPurposeCode: newData.accountPurposeCode,
            financingMethodCode: newData.financingMethodCode,
            mainActivityCode: newData.mainActivityCode,
            personalIdentificationNumber: newData.personalIdentificationNumber,
            gender: newData.gender,
        } as UserProfileUpdate;

        const result = await userApiService.update(objToPost);

        this.setState({ isLoading: false });

        if (result.success) {
            history.push(`/user-profile/${newData.id}`);
        } else {
            toastService.error(result.message);
        }
    };

    addorEditAddress = (type: string, address: UserAddress) => {
        address.type = type;
        this.setState({ isAddressDetailVisible: true, selectedAddress: address });
    };

    onAddressObj = (obj: UserAddress | null | undefined): UserAddress => {
        if (obj === null || obj === undefined) {
            return {
                countryId: "",
                city: "",
                stateProvinceCounty: "",
                zipCode: "",
                addressLine: "",
            };
        }
        return obj;
    };

    onAddressDetailsModalClose = async () => {
        this.setState({ isAddressDetailVisible: false });
    };

    renderAddress = (rowData: UserAddress) => {
        return (
            <span>
                {rowData?.addressLine} {rowData?.city} {rowData?.stateProvinceCounty} {rowData?.zipCode} {this.countryNames.get(rowData?.countryId)}
            </span>
        );
    };

    onAddressUpdate = (address: UserAddress) => {
        const userDataDetail = this.state.userData;
        const normalizedAddressLine = address.addressLine?.split(",")[0].trim() ?? "";

        const objToPost: UserAddress = {
            countryId: address.countryId,
            city: address.city,
            stateProvinceCounty: address.stateProvinceCounty,
            zipCode: address.zipCode,
            addressLine: normalizedAddressLine,
        } as UserAddress;

        if (address.type === "correspondence") {
            userDataDetail!.correspondenceAddress = objToPost;
        } else {
            userDataDetail!.residenceAddress = objToPost;
        }

        this.setState({ userData: userDataDetail, isAddressDetailVisible: false });
    };

    checkStructuredAddressAvailability = async () => {
        if (!isSuperAdmin() && !isComplianceOfficer() && !isComplianceOfficerApprover()) {
            this.setState({ 
                structuredAddressLoading: false, 
                showStructuredAddress: false 
            });
            return;
        }
        if (!this.state.userData?.id) {
            this.setState({ structuredAddressLoading: false, showStructuredAddress: false });
            return;
        }

        this.setState({ structuredAddressLoading: true });

        try {
            const result = await structuredAddressApiService.checkStructuredAddressAvailability(this.state.userData.id);
            
            if (result.success && result.data) {
                this.setState({ 
                    showStructuredAddress: result.data.canManageStructuredAddress,
                    structuredAddressLoading: false 
                });
            } else {
                this.setState({ 
                    showStructuredAddress: false,
                    structuredAddressLoading: false 
                });
                
                if (result.message) {
                    toastService.info("Structured address feature is not available for this user");
                }
            }
        } catch (error) {
            console.error('Error checking structured address availability:', error);
            this.setState({ 
                showStructuredAddress: false,
                structuredAddressLoading: false 
            });
        }
    };

    // TNPDEVAPPS-1954: the residence permit group needs both ids to work — its GET reads the
    // individual and its save POST routes by the KYC submission — so without either the section
    // stays hidden. Roles follow the Structured Address gate above.
    canShowResidencePermitSection = (): boolean =>
        (isSuperAdmin() || isComplianceOfficer() || isComplianceOfficerApprover())
        && (this.state.userData?.id ?? 0) > 0
        && (this.state.userData?.kycSubmissionId ?? 0) > 0;

    render() {
        if (this.state.isLoading || this.state.userData == null) {
            return (
                <div>
                    <Row className="align-center">
                        <LoadingDots />
                    </Row>
                </div>
            );
        }
        // TODO. Handle Criteria.
        const isReadOnly =
            this.state.userData.kycStatus != null && this.props.kycStatusTypes.get(this.state.userData.kycStatus) !== "New" ? true : false;

        return (
            <Fragment>
                {this.state.isAddressDetailVisible && (
                    <AddUserAddress
                        onHide={this.onAddressDetailsModalClose}
                        address={this.state.selectedAddress!}
                        countries={this.countries}
                        onAddressUpdate={this.onAddressUpdate}
                    />
                )}

                <div>
                                        <Row>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={3}>
                                                <Label for="UserId">User Id</Label>
                                                <InputText
                                                    value={this.state.userData?.id.toString()}
                                                    className={"form-control"}
                                                    id="id"
                                                    readOnly={true}
                                                />
                                            </Col>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={3}>
                                                <Label for="UserName">User Name</Label>
                                                <InputText
                                                    value={this.state.userData?.email}
                                                    className={"form-control"}
                                                    id="UserName"
                                                    readOnly={true}
                                                />
                                            </Col>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={3}>
                                                <Label for="FirstName">First Name</Label>
                                                <InputText
                                                    value={this.state.userData?.firstName}
                                                    className={"form-control"}
                                                    id="firstName"
                                                    readOnly={true}
                                                />
                                            </Col>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={3}>
                                                <Label for="LastName">Last Name</Label>
                                                <InputText
                                                    value={this.state.userData?.lastName}
                                                    className={"form-control"}
                                                    id="lastName"
                                                    readOnly={true}
                                                />
                                            </Col>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={3}>
                                                <Label for="EmailAddress">Email Address</Label>
                                                <InputText value={this.state.userData?.email} className={"form-control"} id="email" readOnly={true} />
                                            </Col>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={3}>
                                                <Label for="RegisterationDate">Registeration Date</Label>
                                                <Calendar
                                                    id="registerationStamp"
                                                    className="w-full"
                                                    value={new Date(this.state.userData?.registerationStamp)}
                                                    disabled={true}
                                                />
                                            </Col>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={3}>
                                                <Label for="Merchant">Merchant</Label>
                                                <InputText
                                                    value={this.state.userData?.merchant}
                                                    className={"form-control"}
                                                    id="merchant"
                                                    readOnly={true}
                                                />
                                            </Col>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={3}>
                                                <Label for="MerchantGroup">Merchant Group</Label>
                                                <ExtendedDropdown
                                                    name="merchantGroup"
                                                    style={{ display: "flex" }}
                                                    value={this.state.userData?.merchantGroupId}
                                                    options={this.props.groupsSelectItem}
                                                    filter
                                                    filterBy="label"
                                                    required={true}
                                                    onChange={(e) => this.onFieldUpdate("merchantGroupId", e.value)}
                                                    placeholder={"Select Merchant Group"}
                                                    onValidStatusChanged={(value: boolean) => this.validationErrors.set("merchantGroup", value)}
                                                    disabled={isReadOnly}
                                                />
                                            </Col>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={3}>
                                                <Label for="Mobile">Mobile</Label>
                                                <InputText
                                                    value={this.state.userData?.phoneNumber}
                                                    className={"form-control"}
                                                    id="phoneNumber"
                                                    onChange={(e) => this.onFieldUpdate("phoneNumber", e.target.value)}
                                                    readOnly={true}
                                                />
                                            </Col>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={3}>
                                                <Label for="DateofBirth">Date of Birth</Label>
                                                <Calendar
                                                    value={new Date(this.state.userData?.birthDate)}
                                                    className="w-full"
                                                    id="birthDate"
                                                    maxDate={new Date()}
                                                    onChange={(e) => this.onFieldUpdate("birthDate", e.value)}
                                                    disabled={isReadOnly}
                                                />
                                            </Col>

                                            <Col className="p-5" sm={12} md={4} lg={4} xl={3}>
                                                <Label for="personalIdentificationNumber">Personal Identification Number</Label>
                                                <InputText
                                                    value={this.state.userData?.personalIdentificationNumber}
                                                    className={"form-control"}
                                                    id="personalIdentificationNumber"
                                                    onChange={(e) => this.onFieldUpdate("personalIdentificationNumber", e.target.value)}
                                                    minLength={9}
                                                    maxLength={20}
                                                    readOnly={true}
                                                />
                                            </Col>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={3}>
                                                <Label for="Gender">Gender</Label>
                                                <ExtendedDropdown
                                                    name="gender"
                                                    style={{ display: "flex" }}
                                                    value={this.state.userData?.gender}
                                                    options={this.genderSelectItem}
                                                    filter
                                                    filterBy="label"
                                                    required={true}
                                                    onChange={(e) => this.onFieldUpdate("gender", e.value)}
                                                    placeholder={"Please Select Gender"}
                                                    onValidStatusChanged={(value: boolean) => this.validationErrors.set("gender", value)}
                                                    disabled={isReadOnly}
                                                />
                                            </Col>
                                            <Col className="p-5" sm={12} md={6} lg={8} xl={6}>
                                                <Label for="SourceofFunds">Source of Funds</Label>
                                                <ExtendedDropdown
                                                    style={{ display: "flex" }}
                                                    value={this.state.userData?.sourceOfFundId.toString()}
                                                    options={this.sourceOfFunds}
                                                    onChange={(e) => this.onFieldUpdate("sourceOfFundId", e.value)}
                                                    filter
                                                    filterBy="label"
                                                    required={true}
                                                    name="Source Of Funds"
                                                    onValidStatusChanged={(value: boolean) => this.validationErrors.set("sourceOfFunds", value)}
                                                    disabled={isReadOnly}
                                                />
                                            </Col>

                                            <Col className="p-5" sm={12} md={4} lg={4} xl={6}>
                                                <Label for="AddressOfResidence">Address Of Residence</Label>

                                                <div className="square border rounded border-2 p-2">
                                                    {this.renderAddress(this.state.userData?.residenceAddress)}
                                                    <button
                                                        type="button"
                                                        className="bg-transparent border-0 cursor-pointer p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors float-right"
                                                        tabIndex={0}
                                                        onClick={() =>
                                                            this.addorEditAddress(
                                                                "residence",
                                                                this.onAddressObj(this.state.userData?.residenceAddress),
                                                            )
                                                        }
                                                        disabled={isReadOnly}
                                                    >
                                                        <Pencil size={16} className="cursor-pointer text-neutral-500 hover:text-primary-500 transition-colors" />
                                                    </button>
                                                </div>
                                            </Col>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={6}>
                                                <Label for="AddressOfCorrespondence">Address Of Correspondence</Label>

                                                <div className="square border rounded border-2 p-2">
                                                    {this.renderAddress(this.state.userData?.correspondenceAddress)}
                                                    <button
                                                        type="button"
                                                        className="bg-transparent border-0 cursor-pointer p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors float-right"
                                                        tabIndex={0}
                                                        onClick={() =>
                                                            this.addorEditAddress(
                                                                "correspondence",
                                                                this.onAddressObj(this.state.userData?.correspondenceAddress),
                                                            )
                                                        }
                                                        disabled={isReadOnly}
                                                    >
                                                        <Pencil size={16} className="cursor-pointer text-neutral-500 hover:text-primary-500 transition-colors" />
                                                    </button>
                                                </div>
                                            </Col>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={6}>
                                                <Label for="Nationalities">Nationalities</Label>
                                                <MultiSelect
                                                    id="nationalities"
                                                    style={{ display: "flex" }}
                                                    filter
                                                    filterBy="label"
                                                    optionLabel="label"
                                                    optionValue="value"
                                                    showSelectAll={false}
                                                    value={this.state.userData?.nationalities}
                                                    options={this.countries}
                                                    onChange={(e) => this.updateNationalities(e.value)}
                                                    display="chip"
                                                    //selectedItemTemplate={this.selectedCountryTemplate}
                                                    itemTemplate={this.countryOptionTemplate}
                                                    disabled={isReadOnly}
                                                />
                                            </Col>

                                            <Col className="p-5" sm={12} md={4} lg={4} xl={6}>
                                                <Label for="TaxResidence1">Tax Residence</Label>
                                                <MultiSelect
                                                    id="taxResidences"
                                                    style={{ display: "flex" }}
                                                    filter
                                                    filterBy="label"
                                                    optionLabel="label"
                                                    optionValue="value"
                                                    showSelectAll={false}
                                                    value={this.state.userData?.taxResidences}
                                                    options={this.countries}
                                                    onChange={(e) => this.updateTaxResidence(e.value)}
                                                    display="chip"
                                                    // selectedItemTemplate={this.selectedCountryTemplate}
                                                    itemTemplate={this.countryOptionTemplate}
                                                    disabled={isReadOnly}
                                                />
                                            </Col>

                                            <Col className="p-5" sm={12} md={4} lg={4} xl={4}>
                                                <Label for="NationalIdNumber">National Id Number</Label>
                                                <InputText
                                                    value={this.state.userData?.nationalIdNumber}
                                                    className={"form-control"}
                                                    id="nationalIdNumber"
                                                    onChange={(e) => this.onFieldUpdate("nationalIdNumber", e.target.value)}
                                                    readOnly={isReadOnly}
                                                />
                                            </Col>

                                            <Col className="p-5" sm={12} md={4} lg={4} xl={4}>
                                                <Label for="PrimaryLanguage">Primary Language</Label>
                                                <ExtendedInputText
                                                    name="primaryLanguage"
                                                    value={this.state.userData?.primaryLanguage}
                                                    required={true}
                                                    onChange={(value) => this.onFieldUpdate("primaryLanguage", value)}
                                                    onValidStatusChanged={(value: boolean) => this.validationErrors.set("primaryLanguage", value)}
                                                    readOnly={isReadOnly}
                                                />
                                            </Col>
                                            <Col className="p-5" sm={12} md={4} lg={4} xl={4}>
                                                <Label for="SecondaryLanguage">Secondary Language</Label>
                                                <InputText
                                                    value={this.state.userData?.secondaryLanguage}
                                                    className={"form-control"}
                                                    id="secondaryLanguage"
                                                    onChange={(e) => this.onFieldUpdate("secondaryLanguage", e.target.value)}
                                                    readOnly={isReadOnly}
                                                />
                                            </Col>
                                        </Row>
                                        {this.state.showStructuredAddress && (
                                            <Row>
                                                <StructuredAddressComponent
                                                    customerNo={this.state.userData?.id?.toString() || ""}
                                                    toast={null}
                                                />
                                            </Row>
                                        )}
                                        {this.state.structuredAddressLoading && (
                                            <Row>
                                                <Col sm={12} style={{ textAlign: 'center', padding: '20px' }}>
                                                    <LoadingDots />
                                                    <div>Checking structured address availability...</div>
                                                </Col>
                                            </Row>
                                        )}
                                        {this.canShowResidencePermitSection() && (
                                            <Row>
                                                <ResidencePermitSection
                                                    individualId={this.state.userData!.id}
                                                    submissionId={this.state.userData!.kycSubmissionId}
                                                    showApprovalBlockedBanner={false}
                                                />
                                            </Row>
                                        )}
                                        <Row className="p-5 text-center">
                                            <Col sm={12} md={4} className="p-5">
                                                <label htmlFor="TermAndConditionsAcceptance">Terms And Conditions Acceptance</label>
                                                <div>{this.renderDateBadge(this.state.userData?.termsAndConditionsAcceptanceDate)}</div>
                                            </Col>
                                            <Col sm={12} md={4} className="p-5">
                                                <label htmlFor="PrivacyPolicy">Privacy Policy</label>
                                                <div>{this.renderDateBadge(this.state.userData?.privacyPolicyAcceptanceDate)}</div>
                                            </Col>
                                            <Col sm={12} md={4} className="p-5">
                                                <label htmlFor="TariffAcceptance">Tariff Acceptance</label>
                                                <div>{this.renderDateBadge(this.state.userData?.tariffAcceptanceDate)}</div>
                                            </Col>
                                        </Row>
                                        {isUserProfileUpdateAllowed() && (
                                            <div className="flex justify-end px-5 py-4">
                                                <Button
                                                    type="button"
                                                    disabled={isReadOnly}
                                                    label="Submit"
                                                    icon={<Check size={16} />}
                                                    onClick={this.onSubmitButtonClick}
                                                />
                                            </div>
                                        )}
                </div>
            </Fragment>
        );
    }
}